import { createContext, useContext, useEffect, useState } from "react";
import {
    getDoc,
    onSnapshot,
    collection,
    doc,
    arrayUnion,
    arrayRemove,
    updateDoc,
} from "firebase/firestore";
import { db } from "../firebase/config";
import { createList } from "../firebase/lists";
import { useAuth } from "./AuthContext";

const SharedListContext = createContext();
export const useSharedLists = () => useContext(SharedListContext);

export const SharedListProvider = ({ children }) => {
    const { user } = useAuth();
    const [sharedLists, setSharedLists] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!user) {
            setSharedLists([]);
            setLoading(false);
            return;
        }

        const unsubscribe = onSnapshot(collection(db, "lists"), (snapshot) => {
            const lists = snapshot.docs
                .map((doc) => ({ id: doc.id, ...doc.data() }))
                .filter(
                    (data) =>
                        data.ownerId === user.uid || (data.members || []).includes(user.uid)
                );

            setSharedLists(lists);
            setLoading(false);
        });

        return () => unsubscribe();
    }, [user]);

    const createSharedList = async (listData) => {
        if (!user) throw new Error("No current user");

        // List is already assigned in AddListForm
        const newList = await createList({
            ...listData,
            ownerId: user.uid,
            members: [user.uid],
        });

        setSharedLists((prev) => [...prev, newList]);
        return newList;
    };

    const inviteUser = async (listId, email = null) => {
        const token = crypto.randomUUID();
        const listRef = doc(db, "lists", listId);

        await updateDoc(listRef, {
            [`inviteTokens.${token}`]: email,
        });

        return `${window.location.origin}/list-join?listId=${listId}&token=${token}`;
    };

    const generateInviteLink = (listId) => {
        const token = crypto.randomUUID();
        const listRef = doc(db, "lists", listId);
        updateDoc(listRef, {
            [`inviteTokens.${token}`]: null,
        }).catch((err) =>
            console.error("Error generating invite token:", err)
        );

        return `${window.location.origin}/public-list/${listId}?token=${token}`;
    };

    const joinListWithToken = async (listId, token, selectedName = null) => {
        if (!user) throw new Error("No current user");

        const listRef = doc(db, "lists", listId);
        const listSnap = await getDoc(listRef);
        if (!listSnap.exists()) throw new Error("List not found");

        const listData = listSnap.data();

        // Update participant with UID if joining
        if (listData.isSecretSanta && selectedName) {
            const updatedParticipants = listData.participants.map((p) =>
                p.name === selectedName ? { ...p, userId: user.uid } : p
            );
            await updateDoc(listRef, { participants: updatedParticipants });
        }

        let displayName =
            user.firstName && user.lastName
                ? `${user.firstName} ${user.lastName}`
                : user.displayName || "Member";

        await updateDoc(listRef, {
            members: arrayUnion(user.uid),
            [`inviteTokens.${token}`]: null,
            memberInfo: arrayUnion({
                uid: user.uid,
                displayName,
            }),
        });
    };

    const leaveSharedList = async (listId) => {
        if (!user) throw new Error("No current user");

        const listRef = doc(db, "lists", listId);
        await updateDoc(listRef, { members: arrayRemove(user.uid) });

        setSharedLists((prev) => prev.filter((list) => list.id !== listId));
    };

    return (
        <SharedListContext.Provider
            value={{
                sharedLists,
                loading,
                createSharedList,
                inviteUser,
                joinListWithToken,
                leaveSharedList,
                generateInviteLink,
            }}
        >
            {children}
        </SharedListContext.Provider>
    );
};
